import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase.ts';
import { useTenant } from '../providers/TenantProvider.tsx';
import { Plan } from '../lib/db/types.ts';

export function PlansPage() {
  const { tenantId, role } = useTenant();
  const [plans, setPlans] = useState<(Plan & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<(Plan & { id: string }) | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    features: '',
    price: '',
    active: true,
  });

  const isOwnerOrManager = role === 'owner' || role === 'manager';

  // Load plans
  useEffect(() => {
    if (!tenantId) return;
    loadPlans();
  }, [tenantId]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'plans'),
        where('tenantId', '==', tenantId)
      );
      const snapshot = await getDocs(q);
      const plansData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as (Plan & { id: string })[];
      
      setPlans(plansData);
    } catch (error: any) {
      console.error('Error loading plans:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan?: Plan & { id: string }) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description,
        features: plan.features.join('\n'),
        price: plan.price ? (plan.price / 100).toString() : '',
        active: plan.active,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        features: '',
        price: '',
        active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlan(null);
  };

  const handleSavePlan = async () => {
    try {
      const features = formData.features
        .split('\n')
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

      const price = formData.price
        ? Math.round(parseFloat(formData.price) * 100)
        : undefined;

      const planData = {
        tenantId,
        name: formData.name,
        description: formData.description || null,
        features,
        price: price || 0,
        active: formData.active,
        updatedAt: Timestamp.now(),
      };

      if (editingPlan) {
        // Update existing plan
        await updateDoc(doc(db, 'plans', editingPlan.id), planData);
        setMessage({ type: 'success', text: 'Plan updated successfully' });
      } else {
        // Create new plan
        await addDoc(collection(db, 'plans'), {
          ...planData,
          createdAt: Timestamp.now(),
        });
        setMessage({ type: 'success', text: 'Plan created successfully' });
      }

      handleCloseDialog();
      loadPlans();
    } catch (error: any) {
      console.error('Error saving plan:', error);
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;

    try {
      await deleteDoc(doc(db, 'plans', planId));
      setMessage({ type: 'success', text: 'Plan deleted successfully' });
      loadPlans();
    } catch (error: any) {
      console.error('Error deleting plan:', error);
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleSyncToStripe = async (planId: string) => {
    try {
      setSyncing(planId);
      setMessage(null);

      const syncPlanToStripe = httpsCallable(functions, 'syncPlanToStripe');
      const result = await syncPlanToStripe({
        planId,
      });

      const data = result.data as any;
      setMessage({
        type: 'success',
        text: `Synced to Stripe! Product ID: ${data.stripeProductId}`,
      });
      
      loadPlans();
    } catch (error: any) {
      console.error('Error syncing plan:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSyncing(null);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Membership Plans
        </Typography>
        {isOwnerOrManager && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Plan
          </Button>
        )}
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {plans.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              No plans yet. Create your first membership plan to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} md={6} lg={4} key={plan.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Typography variant="h6" component="h2">
                      {plan.name}
                    </Typography>
                    <Chip
                      label={plan.active ? 'Active' : 'Inactive'}
                      color={plan.active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Typography color="text.secondary" gutterBottom>
                    {plan.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Features:
                  </Typography>
                  <List dense>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={`• ${feature}`} />
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Pricing:
                  </Typography>
                  {plan.price && (
                    <Typography variant="body2">
                      ${(plan.price / 100).toFixed(2)}/month
                    </Typography>
                  )}

                  {plan.stripeProductId && (
                    <Box mt={2}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Stripe Sync:
                      </Typography>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`Product: ${plan.stripeProductId.substring(0, 20)}...`}
                        color="success"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      {plan.priceIds && plan.priceIds.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {plan.priceIds.length} price(s) synced
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>

                {isOwnerOrManager && (
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={syncing === plan.id ? <CircularProgress size={16} /> : <SyncIcon />}
                      onClick={() => handleSyncToStripe(plan.id)}
                      disabled={syncing === plan.id}
                    >
                      {plan.stripeProductId ? 'Re-sync' : 'Sync to Stripe'}
                    </Button>
                    <IconButton size="small" onClick={() => handleOpenDialog(plan)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePlan(plan.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Plan Name"
              value={formData.name}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Features (one per line)"
              value={formData.features}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, features: e.target.value })}
              fullWidth
              multiline
              rows={4}
              helperText="Enter each feature on a new line"
            />

            <TextField
              label="Monthly Price (USD)"
                value={formData.price}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, price: e.target.value })}
              fullWidth
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              helperText="Enter price in dollars (e.g., 49.99)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSavePlan} variant="contained" disabled={!formData.name}>
            {editingPlan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
